import moment from "moment";
import React, { useEffect } from "react";
import DocumentTitle from "react-document-title";
import { useHistory, useParams } from "react-router";
import { ISignature } from "../../../services/SignaturesService";
import scrollToTop from "../../../utils/scrollToTop";
import * as E from "./ConvenienceTestElementsStyles";
import { Separator } from "office-ui-fabric-react/lib/Separator";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { useDispatch } from "react-redux";
import ElementsService from "../../../services/ElementsService";
import { setSavingsPlanElementsStatus } from "../../../store/auth/actions";
import { SignatureComponent } from "../../Signature/SignatureComponent";
import SampleProfileService from "../../../services/SampleProfile";
import { UserIdentityType } from "../../../services/UserIdentitiesService";
import SavingsPlanService from "../../../services/SavingsPlanService";
import ModalElements from "../../../screen/Elements/ModalElements";
import { TicketSupportContainer } from "../../TicketSupport/TicketSupport.style";
import TicketSupport from "../../TicketSupport/TicketSupport";
import TicketSupportService, {
  TicketSupportData,
  TicketSupportError,
} from "../../../services/TicketSupportService";
import {
  MODAL_BODY_ERROR,
  MODAL_TITLE_ERROR,
  MODAL_TITLE_SUCCESS,
  PARTIAL_MODAL_BODY_SUCCESS,
  ScreenStepType,
} from "../../TicketSupport/TicketSupportConstants";
import { ModalWithCancel } from "../../Modals/ModalWithCancel";

const ConvenienceTestElementsConfirm = (props: any) => {
  const history = useHistory();
  const [modalShow, setModalShow] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [signatures, setSignatures] = React.useState<ISignature[]>([]);
  const [phoneNumbers, setPhoneNumbers] = React.useState<string>("");
  const [userIdentities, setUserIdentities] = React.useState<
    UserIdentityType[]
  >([]);
  const { savingPlanId } = useParams<any>();
  const [sampleProfileId, setSampleProfileId] = React.useState<number>(0);
  const [modalErrorShow, setModalErrorShow] = React.useState(false);
  const dispatch = useDispatch();

  // scroll
  React.useEffect(() => {
    scrollToTop();
  }, []);

  // ticket soporte + modal
  const [showSupportForm, setShowSupportForm] = React.useState(false);
  const [showSupportResultModal, setShowSupportResultModal] =
    React.useState(false);
  const [modalSupportResultTitle, setModalSupportResultTitle] =
    React.useState("");
  const [modalSupportResultBody, setModalSupportResultBody] =
    React.useState("");
  const [ticketError, setTicketError] = React.useState<TicketSupportError>({
    status: false,
    message: "",
  });

  // recuperar sampleProfileId
  useEffect(() => {
    SampleProfileService.getSampleProfileBySavingPlanId(
      Number(savingPlanId)
    ).then((sPId: number) => {
      if (sPId === undefined || sPId === 0) {
        console.error(
          "No se ha encontrado un SampleProfile para el SavingsPlanId proporcionado."
        );
        setModalErrorShow(true);
        return;
      }
      console.log("Sample Profile ID >>> ", sPId);
      setSampleProfileId(sPId);
    });
  }, []);

  // savingPlan && userIdentities
  useEffect(() => {
    try {
      setIsLoaded(false);
      // Paso 1: obtener plan y profileId en paralelo
      SavingsPlanService.getSavingsPlan(savingPlanId).then((sp: any) => {
        if (!sp) {
          console.error("Faltan datos esenciales: SavingsPlan");
          setModalErrorShow(true);
          return;
        }

        // Paso 2: extraer userIdentities del savings plan
        const identities =
          sp?.savingsPlansUserIdentities?.map(
            (item: any) => item.userIdentity
          ) || [];
        setUserIdentities(identities);
      });
    } catch (error) {
      console.error("Error cargando datos para firmar contrato", error);
      setModalErrorShow(true);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Elements Amount
  React.useEffect(() => {
    if (signatures.length === 0 && sampleProfileId !== 0) {
      ElementsService.createSampleProfileElementAmount(
        savingPlanId,
        sampleProfileId
      ).then((signatures: ISignature[]) => {
        setSignatures(signatures);
        setIsLoaded(true);
      });
    }
  }, [signatures, savingPlanId, modalShow, sampleProfileId]);

  // expiración de las firmas
  const processExpirationDate = (signs: ISignature[]) => {
    if (signs !== undefined && signs.length > 0) {
      // eslint-disable-next-line array-callback-return
      signs.map((signature: ISignature) => {
        if (signature && typeof signature.expiresAt === "string") {
          signature.expiresAt = moment(signature.expiresAt).toDate();
        }
      });
    }
  };

  // Phone numbers
  useEffect(() => {
    if (signatures !== undefined) {
      setPhoneNumbers(
        signatures
          .map((signature: ISignature) =>
            signature.phone === undefined
              ? ""
              : "X".repeat(signature.phone.length - 3) +
                signature.phone.slice(
                  signature.phone.length - 3,
                  signature.phone.length
                )
          )
          .join(", ")
      );
    }
  }, [signatures]);

  //envío de datos Ok
  const handleSubmit = async () => {
    if (signatures.length > 0 || !modalShow) {
      await dispatch(setSavingsPlanElementsStatus(Number(savingPlanId)));
      scrollToTop();
      setModalShow(true);
    }
  };

  // -------------------------------------------------------------------
  // Ticket soporte
  // -------------------------------------------------------------------
  const handleSupportSend = async (ticketData: TicketSupportData) => {
    try {
      const response = await TicketSupportService.createNewSupportTicket(
        ticketData
      );

      if (response && response.Status === 200) {
        setModalSupportResultTitle(MODAL_TITLE_SUCCESS);
        setModalSupportResultBody(
          `Ticket con referencia: ${
            response.Id ?? "N/A"
          } ${PARTIAL_MODAL_BODY_SUCCESS}`
        );
      } else {
        setModalSupportResultTitle(MODAL_TITLE_ERROR);
        setModalSupportResultBody(MODAL_BODY_ERROR);
      }
    } catch (error) {
      console.error(`${MODAL_TITLE_ERROR} :`, error);
      setModalSupportResultTitle(MODAL_TITLE_ERROR);
      setModalSupportResultBody(MODAL_BODY_ERROR);
    } finally {
      setShowSupportResultModal(true);
    }
  };

  return (
    <>
      <TicketSupportContainer>
        <TicketSupport
          showSupportForm={showSupportForm}
          setShowSupportForm={setShowSupportForm}
          onSupportSend={handleSupportSend}
          component={"ConvenienceTestElementsConfirm"}
          screenName={ScreenStepType.FirmaContratoElements}
          ticketSupportError={ticketError}
        />
      </TicketSupportContainer>
      <DocumentTitle title="IronIA - Elements - Firma de Contrato">
        <React.Fragment>
          <E.HelperStyle />
          <E.ContainerSignature>
            <E.MainContainerSignature>
              <h2 style={{ margin: 0 }}>Elements - Firma de Contrato</h2>
              <Separator />
              {!isLoaded && <Spinner size={SpinnerSize.large} />}
              {isLoaded && signatures.length > 0 && (
                <SignatureComponent
                  signatures={signatures}
                  userIdentities={userIdentities}
                  alreadySignedText=" "
                  submitButtonText="Firmar contrato"
                  cancelButtonText="Volver"
                  headerTextArea={
                    "Introduce el código que te hemos enviado" +
                    (signatures.length > 1
                      ? ` a los números ${phoneNumbers} `
                      : signatures.length === 1
                      ? ` al número ${phoneNumbers} `
                      : " ") +
                    "por SMS para firmar el contrato."
                  }
                  backButtonText="Volver"
                  onBackButtonClicked={() =>
                    history.push("/Elements/Signature")
                  }
                  onCancelButtonClicked={() =>
                    history.push("/Elements/Signature")
                  }
                  requestNewCodeButtonText="Recibir nuevo código"
                  onNewCodeSended={(signatures: any) => {
                    processExpirationDate(signatures);
                    setSignatures(signatures);
                  }}
                  onSignatureChanged={(signatures: any) => {
                    processExpirationDate(signatures);
                    setSignatures(signatures);
                  }}
                  onSigned={handleSubmit}
                />
              )}
            </E.MainContainerSignature>
          </E.ContainerSignature>
          {modalShow && (
            <ModalElements
              setModalShow={setModalShow}
              title={"¡Proceso de firma completado!"}
              message={
                "Enhorabuena, has completado el contrato de suscripción a Elements en IronIA."
              }
              linkTo={"/Elements"}
            />
          )}

          {modalErrorShow && (
            <ModalElements
              setModalShow={setModalErrorShow}
              title={"Ha ocurrido un error"}
              message={
                "No se ha podido completar el contrato de suscripción a Elements. Por favor, inténtalo de nuevo más tarde."
              }
              linkTo={"/"}
            />
          )}
        </React.Fragment>
      </DocumentTitle>

      {/* Modal de confirmación */}
      <ModalWithCancel
        showModal={showSupportResultModal}
        setShowModal={setShowSupportResultModal}
        title={modalSupportResultTitle}
        body={modalSupportResultBody}
        onClose={() => setShowSupportResultModal(false)}
        btnSubmitText="Cerrar"
      />
    </>
  );
};

export default ConvenienceTestElementsConfirm;
